import html2pdf from "html2pdf.js";

interface QuestionOption {
  texto: string;
}

interface QuestionData {
  pregunta: string;
  justificacion?: string;
}

interface ResultItem {
  ok: boolean;
  question?: QuestionData;
  optionSelect?: QuestionOption;
  optionCorrect?: QuestionOption;
}

interface GeneratePDFParams {
  triviaTitle: string;
  playDate: string;
  userName: string;
  userEmail: string;
  totalHits: number;
  total: number;
  percent: number;
  timeElapsed: number;
  result: ResultItem[];
  attempt: number;
}

export const generateTriviaPDF = ({
  triviaTitle,
  playDate,
  userName,
  totalHits,
  total,
  percent,
  timeElapsed,
  result,
  attempt,
}: GeneratePDFParams) => {
  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}m ${sec < 10 ? "0" : ""}${sec}s`;
  };

  const reportHTML = `
    <div class="report-wrapper">
      <style>
        body, * {
          font-family: "Inter", Arial, sans-serif;
          color: #1d1d1f;
          line-height: 1.4;
        }
        .report-wrapper { padding: 20px; background: #fff; }
        h2 { font-size: 18px; font-weight: 600; margin-bottom: 10px; }

        /* HEADER */
        .containerSection1 {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
          page-break-inside: avoid;
        }
        .containerSection1 > .card-reporte { flex: 0 0 60%; }
        .containerSection1 > .containerMedalTime { flex: 0 0 35%; }

        .card-reporte {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          background: #fff;
          page-break-inside: avoid;
        }
        .card-reporte p { margin: 6px 0; font-size: 14px; }
        .card-reporte strong { font-weight: 600; }

        /* MEDALLA */
        .card-medal {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .card-medal h3 { font-size: 14px; font-weight: 500; color: #6b7280; margin: 0; }
        .card-medal p { font-size: 16px; font-weight: 600; margin: 0; }
        .card-medal-icon p { font-size: 40px; margin: 0; }

        /* PRECISIÓN */
        .card-precision { font-size: 14px; }
        .card-precision span { font-weight: 500; }
        .progress-bar {
          margin-top: 6px;
          width: 100%;
          height: 10px;
          border-radius: 6px;
          background: #e5e7eb;
          overflow: hidden;
        }
        .progress { height: 100%; background: #22c55e; }

        /* STATS */
        .stats {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin: 20px 0;
        }
        .stat {
          flex: 1;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 10px 10px;
          font-size: 14px;
          color: #6b7280;
        }
        .stat span {
          display: block;
          font-weight: 700;
          font-size: 18px;
          margin-bottom: 3px;
        }

        /* PREGUNTAS */
        /* Tarjeta general */
        .question {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          background: #fff;
          box-shadow: 0px 4px 8px rgba(0,0,0,0.15);
          page-break-inside: avoid;
        }

        /* Header */
        .question-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        /* Badge de Pregunta */
        .question-header h4 {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          background: #f3f4f6; /* gris claro */
          padding: 5px 8px;
          padding-bottom: 20px;
          display:flex;
          justify-content:center;
          align-items:center;
          border-radius: 8px;
          color: #374151;
        }

        /* Badge de Correcto/Incorrecto */
        .question-badge {
          font-size: 14px;
          font-weight: 600;
          padding: 5px 10px;
          padding-bottom: 20px;
          border-radius: 8px;
          color: #065f46; /* verde oscuro */
        }

        .question.correct .question-badge {
          background: #d1fae5; /* verde muy claro */
          color: #065f46;
        }

        .question.incorrect .question-badge {
          background: #fee2e2; /* rojo muy claro */
          color: #991b1b;
        }

        /* Texto */
        .question p {
          margin: 6px 0;
          font-size: 14px;
          color: #374151;
        }
        
        .questions h2{
          margin-bottom: 20px;
        }

        .question strong {
          font-weight: 600;
        }

        .card-principal{
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .title-card{
          font-size: 14px;
          color: #6b7280;
        }
      </style>

      <div class="containerSection1">
        <div class="card-reporte card-principal">
            <div><p class="title-card">Nombre de la trivia:</p><p><strong>${triviaTitle}</strong></p></div>
            <div><p class="title-card">Fecha jugada:</p><p><strong>${playDate}</strong></p></div>
            <div><p class="title-card">Realizada por:</p><p><strong>${userName}</strong></p></div>
        </div>
        <div class="containerMedalTime">
            <div class="card-reporte card-precision">
                <span><strong>Precisión:</strong> <br>${percent}%</span><br><br>
                <div class="progress-bar"><div class="progress" style="width:${percent}%"></div></div>
            </div>
        </div>
      </div>
    
      <div class="stats">
        <div class="stat">Preguntas <span>${total}</span> </div>
        <div class="stat">Correctas <span>${totalHits}</span></div>
        <div class="stat">Incorrectas <span>${total - totalHits}</span> </div>
        <div class="stat">Intento <span>${attempt}</span> </div>
        <div class="stat">Tiempo <span>${formatTime(timeElapsed)}</span> </div>
      </div>

      <div class="questions">
        <h2>Reporte detallado</h2>
        ${result
          .map(
            (r, i) => `
    <div class="question ${r.ok ? "correct" : "incorrect"}">
      <div class="question-header">
        <h4>Pregunta ${i + 1}</h4>
        <span class="question-badge">${r.ok ? "Correcto" : "Incorrecto"}</span>
      </div>
      <p><strong>${r.question?.pregunta || ""}</strong></p>
      <p>Tu respuesta:<br> <strong>${r.optionSelect?.texto || "—"}</strong></p>
      <p>Respuesta correcta:<br> <strong>${r.optionCorrect?.texto || "—"}</strong></p>
      ${
        r.question?.justificacion
          ? `<p>Justificación:<br><strong>${r.question.justificacion}</strong></p>`
          : ""
      }
    </div>`,
          )
          .join("")}

      </div>
    </div>
  `;

  const opt = {
    margin: [30, 10, 30, 10] as [number, number, number, number],
    filename: `informe-trivia-${Date.now()}.pdf`,
    image: { type: "jpeg" as const, quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
  };

  html2pdf()
    .set(opt)
    .from(reportHTML)
    .toPdf()
    .get("pdf")
    .then((pdf) => {
      const pageCount = pdf.internal.getNumberOfPages();

      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        pdf.addImage("/images/vpro_logo_main.png", "PNG", 15, 10, 60, 13.2);

        const pageHeight = pdf.internal.pageSize.getHeight();
        const marginBottom = 10;

        const logoFooterWidth = 30;
        const logoFooterHeight = 12;
        const logoY = pageHeight - marginBottom - logoFooterHeight;

        pdf.addImage(
          "/images/logoLugnia.png",
          "PNG",
          15,
          logoY,
          logoFooterWidth,
          logoFooterHeight,
        );

        pdf.setFontSize(8);
        pdf.setTextColor(120);
        const textY = logoY + logoFooterHeight / 2 + 2.5;

        pdf.text(
          "Virtualpro - Lugnia SAS · Gestionamos conocimiento para construir un mundo mejor · www.virtualpro.co",
          15 + logoFooterWidth + 5,
          textY,
          { align: "left" },
        );
      }

      pdf.save(`informe-trivia-${Date.now()}.pdf`);
    });
};
